import { useState } from 'react';
import {
    SHIP_UPGRADE_REFUND_MAX,
    SHIP_UPGRADE_REFUND_MIN,
    getShipDefinition,
    type PlayerRace,
    type TickCheckpointEventOf,
} from '@kaeliux/shared';
import { calculateRushCost } from '@kaeliux/game-engine';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { getShipIcon } from '../utils/shipIcons';
import { formatCountdown } from '../utils/formatters';
import QueueItemCard from './QueueItemCard';

const REFUND_RANGE_LABEL = `${SHIP_UPGRADE_REFUND_MIN * 100}–${SHIP_UPGRADE_REFUND_MAX * 100}%`;

export interface ShipUpgradeQueueItemProps {
    race: PlayerRace;
    event: TickCheckpointEventOf<'shipUpgrade'>;
    now: number;
    /** Hides Cancel for a viewer who may watch this item but not change it. */
    canCancel: boolean;
    onCancel: () => Promise<void>;
    /** Rush is rendered only when this is given. */
    onRush?: () => Promise<void>;
    /** The Dark Matter balance Rush is checked against; only read when `onRush` is given. */
    darkMatter?: number;
}

/** One queued/upgrading entry within a ship's own upgrade card — mirrors ZonePage's QueuedUpgradeRow. */
export default function ShipUpgradeQueueItem({ race, event, now, canCancel, onCancel, onRush, darkMatter = 0 }: ShipUpgradeQueueItemProps) {
    const { openDialog } = useConfirmDialog();
    const [isCancelling, setIsCancelling] = useState(false);
    const [isRushing, setIsRushing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const def = getShipDefinition(race, event.eventDetails.shipType);
    const started = event.eventDetails.startDate.getTime() <= now;
    const remainingMs = event.endDate.getTime() - now;
    // A not-yet-started item only has its OWN duration collapsed to zero by a
    // rush — it still waits for whatever's ahead of it in the queue — so
    // pricing it off the full remaining-to-completion time (which includes
    // that wait) would charge for wall-clock time the rush can't actually
    // remove (round 3 review, finding #2).
    const rushCost = calculateRushCost(started ? remainingMs / 1_000 : event.eventDetails.durationSeconds);
    const alreadyRushed = event.eventDetails.durationSeconds === 0;

    async function doCancel() {
        setIsCancelling(true);
        setError(null);
        try {
            await onCancel();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Cancel failed');
        } finally {
            setIsCancelling(false);
        }
    }

    async function doRush() {
        if (!onRush) return;
        setIsRushing(true);
        setError(null);
        try {
            await onRush();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Rush failed');
        } finally {
            setIsRushing(false);
        }
    }

    function requestCancel() {
        openDialog({
            title: `Cancel ${def.name} upgrade?`,
            message: `You will be refunded ${REFUND_RANGE_LABEL} of the resources spent.`,
            confirmLabel: 'Cancel upgrade',
            danger: true,
            onConfirm: () => { doCancel().catch(() => { }); },
        });
    }

    function requestRush() {
        openDialog({
            title: `Rush ${def.name} upgrade?`,
            message: started
                ? `Instantly complete this for ${rushCost} Dark Matter. This is non-refundable.`
                : `Remove this item's own upgrade time for ${rushCost} Dark Matter. It will still wait for the item ahead of it in the queue. This is non-refundable.`,
            confirmLabel: 'Rush',
            onConfirm: () => { doRush().catch(() => { }); },
        });
    }

    return (
        <QueueItemCard
            image={
                <div className="w-full h-full rounded-control border border-hairline bg-surface-void/60 flex items-center justify-center overflow-hidden">
                    <img src={getShipIcon(race, def.id)} alt="" className="w-full h-full object-contain" />
                </div>
            }
            overlayLabel={`Level ${event.eventDetails.targetLevel}`}
            name={def.name}
            statusText={`${started ? 'Upgrading…' : 'Queued —'} ${formatCountdown(remainingMs)}`}
            error={error}
            isCancelling={isCancelling}
            onCancel={canCancel ? requestCancel : undefined}
            imageBoxClassName="w-20 aspect-[4/3]"
            {...(onRush ? {
                rushCost,
                isRushing,
                rushDisabled: darkMatter < rushCost || alreadyRushed,
                rushDisabledReason: alreadyRushed ? 'Rushed' : undefined,
                onRush: requestRush,
            } : {})}
        />
    );
}
